import { useState, useRef, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { MessageCircle, X, Send, UserCheck, Bot, Loader2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_QUESTIONS = [
  'How do I import customers?',
  'How does SMS pricing work?',
  'How do I track campaign ROI?',
];

export default function ChatWidget() {
  const { getToken } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [mode, setMode] = useState('ai'); // 'ai' or 'human'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastPollTimestampRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load conversation when widget opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    async function loadConversation() {
      setLoadingConversation(true);
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/chat/conversation`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setConversationId(data.conversation_id);
          setMode(data.mode);
          setMessages(data.messages);
          // Set poll timestamp to last message or now
          if (data.messages.length > 0) {
            lastPollTimestampRef.current = data.messages[data.messages.length - 1].created_at;
          } else {
            lastPollTimestampRef.current = new Date().toISOString();
          }
        }
      } catch (err) {
        console.error('Failed to load conversation:', err);
      } finally {
        if (!cancelled) setLoadingConversation(false);
      }
    }

    loadConversation();
    return () => { cancelled = true; };
  }, [isOpen, getToken]);

  // Polling for human mode
  useEffect(() => {
    if (mode !== 'human' || !isOpen || !conversationId) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    async function poll() {
      try {
        const since = lastPollTimestampRef.current || new Date().toISOString();
        const token = await getToken();
        const res = await fetch(
          `${BACKEND_URL}/api/chat/poll?since=${encodeURIComponent(since)}`,
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (res.ok) {
          const data = await res.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
              return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
            });
            lastPollTimestampRef.current = data.messages[data.messages.length - 1].created_at;
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }

    pollRef.current = setInterval(poll, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [mode, isOpen, conversationId, getToken]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const content = text.trim();
    setInput('');
    setLoading(true);

    // Optimistic: add user message immediately
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => {
          // Replace temp message with real one
          const filtered = prev.filter(m => m.id !== tempUserMsg.id);
          const result = [...filtered, data.message];
          if (data.reply) {
            result.push(data.reply);
          }
          return result;
        });
        // Update poll timestamp
        const lastMsg = data.reply || data.message;
        lastPollTimestampRef.current = lastMsg.created_at;
      } else {
        const err = await res.json().catch(() => ({}));
        setMessages(prev => [...prev, {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: err.detail || 'Sorry, something went wrong. Try again.',
          created_at: new Date().toISOString(),
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: 'Network error. Check your connection and try again.',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [loading, getToken]);

  const handleEscalate = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/chat/escalate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMode(data.mode);
        setMessages(prev => [...prev, data.system_message]);
        lastPollTimestampRef.current = data.system_message.created_at;
      }
    } catch (err) {
      console.error('Escalate error:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, getToken]);

  const handleResolve = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/chat/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMode(data.mode);
        setMessages(prev => [...prev, data.system_message]);
        lastPollTimestampRef.current = data.system_message.created_at;
      }
    } catch (err) {
      console.error('Resolve error:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, getToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const isHuman = mode === 'human';

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-20 right-4 sm:right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ width: '380px', height: '500px', maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 8rem)' }}
        >
          {/* Header */}
          <div className={`${isHuman ? 'bg-emerald-600' : 'bg-orange-500'} text-white px-4 py-3 flex items-center justify-between flex-shrink-0 transition-colors`}>
            <div className="flex items-center gap-2">
              {isHuman ? <UserCheck className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              <div>
                <h3 className="font-semibold text-sm">
                  {isHuman ? 'Live Support' : 'Dazza Assistant'}
                </h3>
                <p className="text-xs opacity-80">
                  {isHuman ? 'Connected to support team' : 'Ask me anything about Dazza'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isHuman ? (
                <button
                  onClick={handleResolve}
                  disabled={loading}
                  className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to AI
                </button>
              ) : (
                <button
                  onClick={handleEscalate}
                  disabled={loading}
                  className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                  <UserCheck className="w-3 h-3" />
                  Talk to a human
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-md transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Human mode banner */}
          {isHuman && (
            <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2">
              <p className="text-xs text-emerald-700">
                A human is responding. Replies may take a few minutes.
              </p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loadingConversation ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <p className="text-sm text-gray-500">Loading conversation...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">How can I help?</p>
                  <p className="text-xs text-gray-500">Ask me about Dazza features, pricing, or how to get started.</p>
                </div>
                <div className="space-y-2 w-full">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="w-full text-left text-sm px-3 py-2 bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-200 rounded-lg transition-colors text-gray-700"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-orange-500 text-white rounded-br-sm'
                        : msg.role === 'human'
                        ? 'bg-emerald-100 text-emerald-900 rounded-bl-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}
                  >
                    {msg.role === 'human' && (
                      <span className="text-[10px] font-medium text-emerald-600 block mb-0.5">Support</span>
                    )}
                    {msg.content}
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-xl rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {isHuman ? 'Sending...' : 'Typing...'}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-gray-200 p-3 flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isHuman ? 'Message support...' : 'Ask a question...'}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
              disabled={loading || loadingConversation}
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || loadingConversation}
              className={`px-3 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isHuman
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-orange-500 hover:bg-orange-600'
              }`}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {/* Floating bubble */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 sm:right-6 z-50 w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
        aria-label={isOpen ? 'Close chat' : 'Open chat assistant'}
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}
